import React, { useState } from 'react';
import { MessageCircle, X, Send, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';

let messageSeq = 0;
const nextMessageId = () => `msg-${++messageSeq}`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! I'm here to help with your electrical needs. What can I assist you with today?",
      id: nextMessageId(),
      timestamp: new Date()
    }
  ]);
  const [currentStep, setCurrentStep] = useState('initial');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    service: ''
  });

  // Pre-loaded electrical questions and options
  const quickReplies = {
    initial: [
      { text: "🔌 EV Charger Installation", value: "ev-charger" },
      { text: "⚡ Panel Upgrade Needed", value: "panel-upgrade" },
      { text: "🏠 Smart Home Setup", value: "smart-home" },
      { text: "🔧 Circuit Breaker Issues", value: "breaker-issues" },
      { text: "🚨 Emergency Service", value: "emergency" },
      { text: "💡 Get Free Estimate", value: "estimate" }
    ],
    'ev-charger': [
      { text: "Tesla Wall Connector", value: "tesla" },
      { text: "Level 2 Charger", value: "level2" },
      { text: "Not sure - need advice", value: "advice-ev" }
    ],
    'panel-upgrade': [
      { text: "Frequent breaker trips", value: "trips" },
      { text: "Adding new appliances", value: "appliances" },
      { text: "Old fuse box", value: "old-box" }
    ],
    'smart-home': [
      { text: "Smart lighting", value: "lighting" },
      { text: "Home automation", value: "automation" },
      { text: "Full smart home", value: "full-smart" }
    ],
    'breaker-issues': [
      { text: "Yes, it's an emergency", value: "emergency-yes" },
      { text: "Can wait for appointment", value: "schedule" }
    ]
  };

  const responses = {
    'ev-charger': "Great choice! EV charger installation typically takes 4-6 hours. We handle permits and inspections. What type of charger are you considering?",
    'tesla': "Perfect! Tesla Wall Connectors are our specialty. We can install it with a dedicated 60A circuit for fastest charging. Let me get your details for a free quote.",
    'level2': "Excellent! We install all major Level 2 chargers. Installation includes permit handling and city inspection. Let me collect your information for a free estimate.",
    'advice-ev': "No problem! We'll help you choose the right charger for your vehicle and home setup. Let's get your contact info so we can provide expert recommendations.",
    'panel-upgrade': "Panel upgrades are one of our most common services. This helps with:",
    'trips': "Frequent trips usually mean your panel is overloaded. We can upgrade you to a 200A or 400A panel. Let's schedule a free assessment!",
    'appliances': "Adding EV chargers or major appliances often requires a panel upgrade. We'll assess your needs and provide a detailed quote.",
    'old-box': "Old fuse boxes should definitely be upgraded for safety. Modern panels have better protection and capacity. Let's get you scheduled!",
    'smart-home': "Smart home systems are our specialty! We ensure proper wiring for:",
    'lighting': "Smart lighting transforms your home! We install the necessary neutral wires and smart switches. Ready to get started?",
    'automation': "Full home automation requires proper electrical infrastructure. We'll make sure everything is wired correctly. Let me get your details!",
    'full-smart': "Amazing! A complete smart home includes lighting, thermostats, security, and more. We'll design the perfect system for you. What's your contact info?",
    'breaker-issues': "Circuit breaker problems can range from simple to urgent. How urgent is your situation?",
    'emergency-yes': "🚨 For immediate emergency service, please call us now at (667) 455-6368. We're available 24/7!",
    'schedule': "Smart choice to get it fixed soon! Let me collect your information and we'll schedule an appointment.",
    'emergency': "🚨 For immediate electrical emergencies, call us at (667) 455-6368 - available 24/7!",
    'estimate': "Great! We offer free estimates for all electrical work. Let me get some basic information from you."
  };

  const handleQuickReply = (option) => {
    // Add user message
    setMessages(prev => [...prev, {
      type: 'user',
      text: option.text,
      id: nextMessageId(),
      timestamp: new Date()
    }]);

    // Add bot response
    setTimeout(() => {
      const response = responses[option.value];
      if (response) {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: response,
          id: nextMessageId(),
      timestamp: new Date()
        }]);
      }

      // Update current step
      if (quickReplies[option.value]) {
        setCurrentStep(option.value);
      } else if (['emergency-yes'].includes(option.value)) {
        // Emergency - no form needed
        setCurrentStep('done');
      } else {
        // Show contact form
        setCurrentStep('contact-form');
        setCustomerInfo(prev => ({ ...prev, service: option.text }));
      }
    }, 500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    setMessages(prev => [...prev, {
      type: 'bot',
      text: `Thank you, ${customerInfo.name}! We've received your information and will contact you at ${customerInfo.phone} within 24 hours to discuss your ${customerInfo.service} needs. For immediate assistance, call (667) 455-6368.`,
      id: nextMessageId(),
      timestamp: new Date()
    }]);

    setCurrentStep('done');
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chat-widget-open"
          aria-label="Open chat"
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,215,0,0.5)] hover:scale-110 transition-transform animate-pulse"
        >
          <MessageCircle className="w-8 h-8 text-black" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 z-50 w-96 h-[600px] flex flex-col shadow-2xl border-2 border-yellow-400/50">
          {/* Header */}
          <div className="bg-yellow-400 p-4 flex items-center justify-between rounded-t-lg">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-black">Jetco Assistant</h3>
                <p className="text-xs text-gray-900">Usually replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chat-widget-close"
              aria-label="Close chat"
              className="text-black hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                data-testid={`chat-message-${message.type}`}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.type === 'user'
                      ? 'bg-yellow-400 text-black'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Replies or Form */}
          <div className="p-4 bg-white border-t">
            {currentStep === 'contact-form' ? (
              <form onSubmit={handleSubmit} className="space-y-2">
                <Input
                  placeholder="Your Name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  required
                  className="text-sm"
                />
                <Input
                  placeholder="Phone Number"
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  required
                  className="text-sm"
                />
                <Input
                  placeholder="Email (optional)"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  className="text-sm"
                />
                <Button
                  type="submit"
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Get Free Quote
                </Button>
              </form>
            ) : currentStep === 'done' ? (
              <Button
                onClick={() => {
                  setMessages([{
                    type: 'bot',
                    text: "Hi! I'm here to help with your electrical needs. What can I assist you with today?",
                    id: nextMessageId(),
      timestamp: new Date()
                  }]);
                  setCurrentStep('initial');
                  setCustomerInfo({ name: '', phone: '', email: '', service: '' });
                }}
                className="w-full bg-yellow-400 text-black font-bold"
              >
                Start New Conversation
              </Button>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {quickReplies[currentStep]?.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleQuickReply(option)}
                    data-testid={`chat-quick-reply-${option.value}`}
                    className="w-full text-left p-2 text-sm bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                  >
                    {option.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  );
};

export default ChatWidget;
